import {
    type UserListResponse,
    type UserData,
    type PracticeUserTeamResponse,
} from 'chaika-frontend-types/frontend-cms';

import { axios } from '@lib/axios';
import {
    PracticeContactQuery,
    PracticeContactResponse,
    PracticeContactViewModel,
    UsersPracticeContactResponse,
    OrganizationPracticeOfficeQuery,
    PracticeOfficeGroupListResponse,
    PracticeOfficeGroupViewModel,
} from 'chaika-types';

const BASE_URL = '/api/practice';

export class PracticeApi {
    public static async getUserAssistantsList(userId: string): Promise<UserData[]> {
        try {
            const res = await axios.get<UserListResponse>(`${BASE_URL}/user/${userId}/assistant`);
            return res.data.users;
        } catch (error) {
            console.error('Error fetching user assistants list:', error);
            return [];
        }
    }

    public static async getUserDoctorList(userId: string): Promise<UserData[]> {
        try {
            const res = await axios.get<UserListResponse>(`${BASE_URL}/user/${userId}/doctor`);
            return res.data.users;
        } catch (error) {
            console.error('Error fetching user assistants list:', error);
            return [];
        }
    }

    public static async getUserTeam(userId: string): Promise<PracticeUserTeamResponse> {
        const res = await axios.get<PracticeUserTeamResponse>(`${BASE_URL}/user/${userId}/team`);

        return res.data;
    }

    public static async getUserPracticeData(
        userId: string,
        params: PracticeContactQuery = {},
    ): Promise<PracticeContactViewModel[]> {
        try {
            const res = await axios.get<PracticeContactResponse>(`${BASE_URL}/user/${userId}/contact`, { params });
            return res.data.practiceContacts;
        } catch (error) {
            console.error('Error fetching user practice data:', error);
            return [];
        }
    }

    public static async getUsersPracticeData(
        userIds: string[],
        params: PracticeContactQuery = {},
    ): Promise<Record<string, PracticeContactViewModel[]>> {
        try {
            const res = await axios.get<UsersPracticeContactResponse>(`${BASE_URL}/users/contact`, {
                params: { ...params, userIds: userIds.join(',') },
            });
            return Object.fromEntries(res.data.users.map(({ userId, practiceContacts }) => [userId, practiceContacts]));
        } catch (error) {
            console.error('Error fetching users practice data:', error);
            return {};
        }
    }

    public static async getPracticeData(): Promise<PracticeContactViewModel[]> {
        try {
            const res = await axios.get<PracticeContactResponse>(`${BASE_URL}/contact`);
            return res.data.practiceContacts;
        } catch (error) {
            console.error('Error fetching user practice data:', error);
            return [];
        }
    }

    public static async getOrganizationWalkInPractices(organizationId: string): Promise<PracticeContactViewModel[]> {
        try {
            const res = await axios.get<PracticeContactResponse>(`${BASE_URL}/organization/${organizationId}`);
            return res.data.practiceContacts.filter(({ telegramLink }) => !!telegramLink && telegramLink.length > 0);
        } catch (error) {
            console.error('Error fetching user practice data:', error);
            return [];
        }
    }

    public static async getOrganizationPracticeOffices(
        organizationId: string,
        params?: OrganizationPracticeOfficeQuery,
    ): Promise<PracticeOfficeGroupViewModel[]> {
        const res = await axios.get<PracticeOfficeGroupListResponse>(
            `${BASE_URL}/organization/${organizationId}/office`,
            { params },
        );

        return res.data.practiceOffices;
    }
}
